// src/components/clients/EmailPreviewModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { formatDate } from "./helpers";
import { applyEmailPreviewMode } from "../../services/emailService";

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  emailHtml?: string;
  metadata?: {
    recipient?: string;
    sentAt?: string;
    propertyName?: string;
    propertyCount?: number;
    unitCount?: number;
  };
}

const EMAIL_CANVAS_WIDTH = 600;

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  title,
  emailHtml,
  metadata,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [darkMode, setDarkMode] = useState(false);

  // iframe scaling for mobile — the email HTML is designed at 600px wide,
  // so we scale it down to fit narrow viewports rather than letting the
  // content overflow or clip. ResizeObserver recomputes on orientation
  // change and any layout shift.
  const scaleContainerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(1);
  const [iframeContentHeight, setIframeContentHeight] = useState<number | null>(
    null
  );

  const previewHtml = useMemo(() => {
    if (!emailHtml) return undefined;
    return applyEmailPreviewMode(emailHtml, darkMode ? "dark" : "light");
  }, [emailHtml, darkMode]);

  useEffect(() => {
    if (!isMobile) {
      setScale(1);
      return;
    }
    // The scale-container Box is gated on `previewHtml` being truthy, so
    // on the very first render with `open` flipping true the ref may not
    // be attached yet when this effect fires. Retry via rAF until the
    // ref commits — cheap, bounded, and avoids the open-with-scale-of-1
    // flash where the email overflows the mobile viewport.
    let ro: ResizeObserver | null = null;
    let rafId: number | null = null;
    let cancelled = false;

    const tryAttach = () => {
      if (cancelled) return;
      const node = scaleContainerRef.current;
      if (!node || typeof ResizeObserver === "undefined") {
        rafId = requestAnimationFrame(tryAttach);
        return;
      }
      const compute = () => {
        const w = node.clientWidth;
        if (w > 0) setScale(Math.min(1, w / EMAIL_CANVAS_WIDTH));
      };
      compute();
      ro = new ResizeObserver(compute);
      ro.observe(node);
    };
    tryAttach();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro?.disconnect();
    };
  }, [isMobile, open, previewHtml]);

  // Reset measured height when content changes (mode toggle or new email)
  // so the next iframe onLoad recomputes it — otherwise a dark-mode toggle
  // can leave a stale short/tall reserve box around the scaled email.
  useEffect(() => {
    setIframeContentHeight(null);
  }, [darkMode, emailHtml]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: "#1e293b",
          color: "#e2e8f0",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #334155",
          py: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            fontWeight={600}
            sx={{
              color: "#f1f5f9",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Stack
            direction="row"
            spacing={isMobile ? 0.5 : 1}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButtonGroup
              value={darkMode ? "dark" : "light"}
              exclusive
              size="small"
              onChange={(_e, value) => {
                if (value !== null) setDarkMode(value === "dark");
              }}
              aria-label="Preview color scheme"
              sx={{
                "& .MuiToggleButton-root": {
                  color: "#94a3b8",
                  borderColor: "#475569",
                  padding: isMobile ? "3px 6px" : "4px 10px",
                  fontSize: isMobile ? "0.7rem" : undefined,
                  "&.Mui-selected": {
                    color: "#f1f5f9",
                    backgroundColor: "#334155",
                    "&:hover": { backgroundColor: "#3f4b63" },
                  },
                },
              }}
            >
              <ToggleButton value="light" aria-label="Light mode preview">
                <LightModeIcon
                  fontSize="small"
                  sx={{ mr: isMobile ? 0 : 0.5 }}
                />
                {!isMobile && "Light"}
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark mode preview">
                <DarkModeIcon
                  fontSize="small"
                  sx={{ mr: isMobile ? 0 : 0.5 }}
                />
                {!isMobile && "Dark"}
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: "#94a3b8" }}
              aria-label="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderColor: "#334155",
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {metadata && (
          <Box
            sx={{
              mb: 2,
              p: { xs: 1.25, sm: 2 },
              backgroundColor: "#334155",
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "repeat(auto-fit, minmax(120px, auto))",
                },
                columnGap: { xs: 1.5, sm: 4 },
                rowGap: 1,
              }}
            >
              {metadata.recipient && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Recipient
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e2e8f0",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {metadata.recipient}
                  </Typography>
                </Box>
              )}
              {metadata.sentAt && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Sent
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#e2e8f0", fontWeight: 500 }}
                  >
                    {formatDate(metadata.sentAt)}
                  </Typography>
                </Box>
              )}
              {metadata.propertyName && (
                <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Property
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#e2e8f0",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {metadata.propertyName}
                  </Typography>
                </Box>
              )}
              {metadata.propertyCount !== undefined && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Properties
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#e2e8f0", fontWeight: 500 }}
                  >
                    {metadata.propertyCount}
                  </Typography>
                </Box>
              )}
              {metadata.unitCount !== undefined && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", display: "block" }}
                  >
                    Units
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#e2e8f0", fontWeight: 500 }}
                  >
                    {metadata.unitCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {previewHtml ? (
          <Box
            ref={scaleContainerRef}
            sx={{
              border: "1px solid #334155",
              borderRadius: 2,
              overflow: "hidden",
              // Match the email's outer page background so there's no
              // light-colored seam around the card when dark mode is on.
              backgroundColor: darkMode ? "#18181b" : "#f7f8fc",
              position: "relative",
              // Reserve the scaled height so content after it (if any)
              // doesn't get pushed down by the un-transformed iframe box.
              height:
                isMobile && iframeContentHeight
                  ? `${Math.round(iframeContentHeight * scale + 4)}px`
                  : undefined,
            }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={previewHtml}
              title="Email Preview"
              sandbox="allow-same-origin"
              onLoad={() => {
                const doc = iframeRef.current?.contentDocument;
                if (doc?.body) {
                  setIframeContentHeight(doc.body.scrollHeight + 4);
                }
              }}
              style={{
                width: isMobile ? `${EMAIL_CANVAS_WIDTH}px` : "100%",
                height: iframeContentHeight
                  ? `${iframeContentHeight}px`
                  : "600px",
                border: "none",
                display: "block",
                transform: isMobile ? `scale(${scale})` : undefined,
                transformOrigin: "top left",
              }}
            />
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography sx={{ color: "#64748b" }}>
              Email content not available
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: "1px solid #334155",
          p: { xs: 1.25, sm: 2 },
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            color: "#94a3b8",
            borderColor: "#475569",
            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#33415530" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPreviewModal;
